import { log } from "@/core/logger";

// Global CSRF token storage
// This is set by the CsrfProvider and accessed by fetchWithAuth
let globalCsrfToken: string | null = null;

/**
 * Set the global CSRF token
 * Called by the CsrfProvider when the token is fetched
 * @internal
 */
export function setGlobalCsrfToken(token: string | null) {
  globalCsrfToken = token;
}

/**
 * Get the current global CSRF token
 * @internal
 */
export function getGlobalCsrfToken(): string | null {
  return globalCsrfToken;
}

/**
 * Enhanced fetch wrapper with authentication and CSRF protection
 *
 * Features:
 * - Automatically handles 401 (unauthorized) responses by redirecting to login
 * - Automatically includes CSRF tokens for state-changing requests (POST, PUT, PATCH, DELETE)
 * - Automatically handles 403 (CSRF validation failed) by refetching the token
 * - Maintains credentials (cookies) for all requests
 *
 * @param input - The URL or Request object
 * @param init - Optional RequestInit configuration
 * @returns Promise<Response>
 *
 * @example
 * ```typescript
 * // GET request (no CSRF token needed)
 * const response = await fetchWithAuth('/api/bookings');
 *
 * // POST request (CSRF token automatically included)
 * const response = await fetchWithAuth('/api/bookings', {
 *   method: 'POST',
 *   body: JSON.stringify(data)
 * });
 * ```
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const method = init?.method?.toUpperCase() || "GET";
  const isStatefulRequest = ["POST", "PUT", "PATCH", "DELETE"].includes(method);

  // Prepare headers
  const headers = new Headers(init?.headers || {});

  // Add bearer token for Safari ITP compatibility
  const bearerToken = sessionStorage.getItem('tpr-bearer-token');
  if (bearerToken) {
    headers.set("Authorization", `Bearer ${bearerToken}`);
    log("fetchWithAuth", `Including Bearer token for ${method} request`);
  }

  // Add CSRF token for state-changing requests
  if (isStatefulRequest && globalCsrfToken) {
    headers.set("X-CSRF-Token", globalCsrfToken);
    log("fetchWithAuth", `Including CSRF token for ${method} request`);
  }

  // Ensure credentials are included (required for CSRF cookies)
  const requestInit: RequestInit = {
    ...init,
    headers,
    credentials: init?.credentials || "include",
  };

  const response = await fetch(input, requestInit);

  // Check for unauthorized response
  if (response.status === 401) {
    // Clear any stored user data
    localStorage.removeItem("tpruser");

    // Redirect to login with session expired parameter
    window.location.href = "/login?sessionExpired=true";

    // Throw error to prevent further processing
    throw new Error("Session expired");
  }

  // Check for CSRF validation failure
  if (response.status === 403) {
    // Clone the response so we can read the body without consuming the original
    const clonedResponse = response.clone();
    const errorData = await clonedResponse.json().catch(() => ({}));

    if (errorData.error === "CSRF validation failed") {
      log("fetchWithAuth", "CSRF validation failed - token may be expired or invalid");

      // Note: The CsrfProvider will automatically refetch the token on the next render
      // For now, we'll throw an error to let the caller handle it
      const error = new Error("CSRF token validation failed. Please try again.");
      (error as any).csrfError = true;
      throw error;
    }
  }

  return response;
}
